import { PaginationState, Table } from '@tanstack/react-table';
import cx from 'classnames';
import { range as _range } from 'lodash-es';
import styles from './Paginator.module.scss';

const NR_INDEX_BUTTONS = 5;

interface PaginatorProps<T> {
  table: Table<T>;
  pagination: PaginationState;
  rowCount: number;
  className?: string;
  tableId?: string;
}

const Paginator = <T extends unknown>({
  table,
  pagination: { pageIndex, pageSize } = { pageIndex: 0, pageSize: 0 },
  rowCount,
  className,
  tableId,
}: PaginatorProps<T>) => {
  const pageCount = table.getPageCount();
  const pageIndicesForSelection =
    rowCount <= NR_INDEX_BUTTONS * pageSize || pageIndex <= 1
      ? _range(0, Math.min(pageCount, NR_INDEX_BUTTONS))
      : pageIndex >= pageCount - (NR_INDEX_BUTTONS - 2)
        ? _range(pageCount - NR_INDEX_BUTTONS, pageCount)
        : _range(-2, 3).map(offset => pageIndex + offset);

  const handlePageChange = (callback: () => void) => (e: React.MouseEvent<HTMLButtonElement>) => {
    callback();
    e.currentTarget.blur();
  };

  return (
    <>
      {pageCount > 1 && (
        <div className={cx(styles.paginator, className)}>
          <button
            className={cx(styles.pageBtn)}
            onClick={handlePageChange(() => table.setPageIndex(0))}
            disabled={!table.getCanPreviousPage()}
          >
            {'<<'}
          </button>
          <button
            className={cx(styles.pageBtn)}
            onClick={handlePageChange(() => table.previousPage())}
            disabled={!table.getCanPreviousPage()}
          >
            {'<'}
          </button>
          {pageIndicesForSelection.map(pageIdx => (
            <button
              key={tableId ? `${tableId}-page-${pageIdx}` : `page-${pageIdx}`}
              className={cx(styles.pageBtn, pageIdx === pageIndex ? styles.current : '')}
              onClick={handlePageChange(() => table.setPageIndex(pageIdx))}
              disabled={pageIdx > pageCount - 1}
            >
              {pageIdx + 1}
            </button>
          ))}
          <button
            className={cx(styles.pageBtn)}
            onClick={handlePageChange(() => table.nextPage())}
            disabled={!table.getCanNextPage()}
          >
            {'>'}
          </button>
          <button
            className={cx(styles.pageBtn)}
            onClick={handlePageChange(() => table.setPageIndex(table.getPageCount() - 1))}
            disabled={!table.getCanNextPage()}
          >
            {'>>'}
          </button>
        </div>
      )}
    </>
  );
};

export default Paginator;
